export const AGENT_ROTATION_MS = 6_000;
export const AGENT_PAUSE_MS = 12_000;

type Hold = "hover" | "keyboard" | "hidden" | "offscreen" | "reduced";
export type AgentPlayback = {
  index: number;
  mode: "rotating" | "paused";
  durationMs: number;
  remainingMs: number;
  canResume: boolean;
};
type Clock = {
  now: () => number;
  request: (callback: () => void) => number;
  cancel: (id: number) => void;
};

/** One monotonic clock drives both the state transitions and the painted ring. */
export class AgentCarousel {
  private snapshot: AgentPlayback = {
    index: 0, mode: "rotating", durationMs: AGENT_ROTATION_MS,
    remainingMs: AGENT_ROTATION_MS, canResume: false,
  };
  private holds = new Set<Hold>();
  private listeners = new Set<() => void>();
  private frame: number | null = null;
  private lastTime = 0;
  private running = false;
  private count = 0;

  constructor(private clock: Clock) {}
  getSnapshot = () => this.snapshot;
  getIndex = () => this.snapshot.index;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  configure(count: number) {
    this.count = count;
    if (this.snapshot.index >= count) this.update({ index: 0 });
    this.schedule();
  }
  start() {
    if (this.running) return;
    this.running = true;
    this.lastTime = this.clock.now();
    this.schedule();
  }
  stop() {
    this.running = false;
    this.cancelFrame();
  }
  setHold(hold: Hold, held: boolean) {
    if (this.holds.has(hold) === held) return;
    this.accrue();
    if (held) this.holds.add(hold);
    else this.holds.delete(hold);
    this.lastTime = this.clock.now();
    this.update({});
    this.schedule();
  }
  pause() { this.restart("paused", AGENT_PAUSE_MS); }
  select(index: number) {
    if (index < 0 || index >= this.count) return;
    this.restart("paused", AGENT_PAUSE_MS, index);
  }
  play() {
    // Explicit Play also clears keyboard/reduced-motion opt-in holds. Focus
    // remaining on this button must not reintroduce a keyboard hold.
    this.holds.delete("keyboard");
    this.holds.delete("reduced");
    this.restart("rotating", AGENT_ROTATION_MS);
  }
  private restart(mode: AgentPlayback["mode"], durationMs: number, index = this.snapshot.index) {
    this.lastTime = this.clock.now();
    this.update({ mode, durationMs, remainingMs: durationMs, index });
    this.schedule();
  }
  private update(change: Partial<AgentPlayback>) {
    this.snapshot = {
      ...this.snapshot, ...change,
      canResume: (change.mode ?? this.snapshot.mode) === "paused"
        || this.holds.has("keyboard") || this.holds.has("reduced"),
    };
    this.listeners.forEach((listener) => listener());
  }
  private accrue() {
    const now = this.clock.now();
    if (this.running && this.holds.size === 0 && this.count > 1) {
      const remainingMs = Math.max(0, this.snapshot.remainingMs - Math.max(0, now - this.lastTime));
      if (remainingMs === 0) {
        // No catch-up: even after a delayed frame the next interval is full.
        this.update({
          index: this.snapshot.mode === "paused" ? this.snapshot.index : (this.snapshot.index + 1) % this.count,
          mode: "rotating", durationMs: AGENT_ROTATION_MS, remainingMs: AGENT_ROTATION_MS,
        });
      } else this.update({ remainingMs });
    }
    this.lastTime = now;
  }
  private cancelFrame() {
    if (this.frame !== null) this.clock.cancel(this.frame);
    this.frame = null;
  }
  private schedule() {
    this.cancelFrame();
    if (!this.running || this.holds.size > 0 || this.count <= 1) return;
    this.frame = this.clock.request(() => {
      this.frame = null;
      this.accrue();
      this.schedule();
    });
  }
}
